import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useApolloClient, useQuery } from '@apollo/client'
import { isMobileOnly } from 'react-device-detect'
import { Table } from '@/components_deprecated/Table'
import { ALL_ISSUE_PROCESSES_QUERY } from '@/modules/issues'
import type { AllIssueProcessesQuery } from '@/modules/issues'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import { useIssueProcessMutations } from '../../util/mutations.hooks'
import { OldAddButton, OldSection, SectionHeader } from '../common'
import type {
  IssueProcessChoiceFormItem,
  IssueProcessForm,
} from './issue-process-prompt'
import { IssueProcessPrompt } from './issue-process-prompt'

interface Props {
  loading?: boolean
}

/**
 * Settings section for issue "processes" and their choices: lists the
 * company's processes and drives create/edit/delete through
 * {@link IssueProcessPrompt}. Self-contained — owns its own data fetch and
 * mutations so the parent settings view doesn't have to.
 */
export const IssueProcessSection: React.FC<Props> = ({ loading }) => {
  const translations = useTranslate({
    issueProcess: 'settings.issue-process',
    issueProcesses: 'settings.issue-processes',
    choices: 'settings.issue-process-choices',
    choiceCount: ['settings.issue-process-choice-count', { count: 0 }],
    prompt: {
      delete: ['settings.prompts.issue-process-delete', { name: '' }],
      deleteTitle: 'settings.prompts.issue-process-delete-title',
    },
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const processMutations = useIssueProcessMutations()

  const apollo = useApolloClient()

  const { data, refetch } = useQuery<AllIssueProcessesQuery, never>(
    ALL_ISSUE_PROCESSES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onError: onErrorAuto(),
    }
  )
  const processes = data?.allIssueProcesses?.edges.map(edge => edge.node) ?? []

  // Refresh both the section's list and the parent settings query once, after a
  // whole create/edit batch settles (the choice mutations no longer refetch).
  async function refetchAll() {
    await Promise.all([
      refetch(),
      apollo.refetchQueries({ include: ['AllIssueDataForSettings'] }),
    ])
  }

  type ProcessNode = (typeof processes)[number]

  /** Choices of a process as prompt form items, ordered by their `order`. */
  function getProcessChoices(
    process: ProcessNode
  ): IssueProcessChoiceFormItem[] {
    return (
      process.choices?.edges
        ?.slice()
        .sort((a, b) => a.node.order - b.node.order)
        .map(edge => ({ id: edge.node.id, name: edge.node.name })) ?? []
    )
  }

  async function handleCreatePrompt() {
    const { data: form } = await addPrompt<IssueProcessForm | null>(resolve => (
      <IssueProcessPrompt
        existingProcesses={processes.map(p => ({ id: p.id, name: p.name }))}
        onSubmit={resolve}
      />
    ))
    if (form === null) return

    const { data: createResp } = await processMutations.create({
      variables: { input: { name: form.name } },
    })
    const processId = createResp?.createIssueProcess?.issueProcess?.id
    if (!processId) return

    await Promise.all(
      form.choices.map((choice, index) =>
        processMutations.createChoice({
          variables: {
            input: { process: processId, name: choice.name, order: index },
          },
        })
      )
    )
    await refetchAll()
  }

  async function handleEditPrompt(process: ProcessNode) {
    const original = getProcessChoices(process)
    const { data: form } = await addPrompt<IssueProcessForm | null>(resolve => (
      <IssueProcessPrompt
        initialData={{ name: process.name, choices: original }}
        processId={process.id}
        existingProcesses={processes.map(p => ({ id: p.id, name: p.name }))}
        onSubmit={resolve}
      />
    ))
    if (form === null) return

    if (form.name !== process.name) {
      await processMutations.patch({
        variables: { id: process.id, input: { name: form.name } },
      })
    }

    const submittedIds = new Set(
      form.choices.filter(c => c.id).map(c => c.id as string)
    )
    const removedIds = original
      .filter(c => c.id && !submittedIds.has(c.id))
      .map(c => c.id as string)

    await Promise.all([
      ...removedIds.map(id =>
        processMutations.deleteChoice({ variables: { id } })
      ),
      ...form.choices.map((choice, index) => {
        if (choice.id) {
          const existing = original.find(c => c.id === choice.id)
          const existingOrder =
            process.choices.edges.find(e => e.node.id === choice.id)?.node
              .order ?? 0
          if (existing && existing.name === choice.name && existingOrder === index)
            return null
          return processMutations.patchChoice({
            variables: {
              id: choice.id,
              input: { name: choice.name, order: index },
            },
          })
        }
        return processMutations.createChoice({
          variables: {
            input: { process: process.id, name: choice.name, order: index },
          },
        })
      }),
    ])

    await refetchAll()
  }

  async function handleDeletePrompt(process: ProcessNode) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: process.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void processMutations.delete({ variables: { id: process.id } })
  }

  return (
    <OldSection>
      <header className="flex justify-between items-start">
        <SectionHeader loading={loading || processMutations.loading}>
          {translations.issueProcesses}
        </SectionHeader>
        {!isMobileOnly && (
          <OldAddButton adding={false} onClick={handleCreatePrompt} />
        )}
      </header>
      {processes.length > 0 && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.issueProcess}</th>
              <th>{translations.choices}</th>
              <th colSpan={2} />
            </tr>
          </thead>
          <tbody>
            {processes.map(process => {
              const choices = getProcessChoices(process)
              const choiceNames = choices.map(c => c.name).join(', ')
              return (
                <tr key={process.id}>
                  <td>{process.name}</td>
                  <td title={choiceNames}>
                    {translations.choiceCount({ count: choices.length })}
                  </td>
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEditPrompt(process)}
                    />
                  </td>
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDeletePrompt(process)}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </OldSection>
  )
}
